import { CloseButton, DrawerPositioner, Flex, Portal, Text, Button, Badge } from '@chakra-ui/react';
import {
  DrawerCloseTrigger,
  DrawerHeader,
  DrawerRoot,
  DrawerTitle,
  DrawerTrigger,
  DrawerBackdrop,
  DrawerContent,
  DrawerBody,
} from '@/components/ui/drawer';
import { useProductSearch } from '@/modules/product-context';
import PillButton from '@/components/common/pill-button';
import { Sliders as SlidersIcon } from 'react-feather';
import { useRef, useState, useEffect } from 'react';
import { AccordionItem, AccordionItemContent, AccordionItemTrigger, AccordionRoot } from '@/components/ui/accordion';
import styles from './index.module.css';
import { PinIcon } from './components/pin-icon';
import { CategoryAccordion } from './components/category-accordion';
import { BrandFilter } from './components/brand';
import { GetItemInput } from '@/generated/nabisRetailerAPI.schemas';
import { ClearFilter } from './components/clear-filter';
// import ThcSlider from './components/thc-slider';
import { useRouter } from 'next/router';
import { identifyPage, LinkEnum } from '@/util/link-manager';
import ProductAvailability from './components/product-avalibility';
import StrainType from './components/strain-type';
import { useGlobalContext } from '@/modules/global-context/use-global-context';
import { Pin } from '@/constants/filters';
import PriceSlider from './components/price-slider';
import ExpirationDays from './components/expiration-days';
import Weight from './components/weight';
import { CbdThcRatio } from './components/cbd-thc-ratio';
import ThcSlider from './components/thc-slider';
import useInStockFilter from '@/hooks/use-in-stock-filter';
import { useMarketplaceMode } from '@/modules/marketplace/use-marketplace-mode';

const FiltersDrawer = () => {
  const { canViewPricing } = useMarketplaceMode();
  const { isDrawerOpen, setDrawerOpen, searchParams, onSearch } = useProductSearch();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [totalFiltersApplied, setTotalFiltersApplied] = useState(0);

  const { inStockFilterEnabled, setInStockFilter } = useInStockFilter();

  const {
    meta: { categories },
  } = useGlobalContext();

  const router = useRouter();

  const page = identifyPage(router.pathname);
  const isPublicPage = router.pathname.startsWith('/public');

  const clearFilter = () => {
    const clearParams: Partial<GetItemInput> = {
      strainType: undefined,
      inventoryCategory: undefined,
      thcMin: undefined,
      thcMax: undefined,
      ...(page !== LinkEnum.BRAND_DETAILS && { brandId: undefined }),
      classCategoryGroup: undefined,
      cbdThcRatio: undefined,
      expirationDays: undefined,
      weightRange: undefined,
      priceMin: undefined,
      priceMax: undefined,
    };
    onSearch(clearParams);
    setInStockFilter(false);
  };

  useEffect(() => {
    let count = 0;
    if (searchParams?.brandId?.length) {
      count++;
    }
    if (searchParams.strainType) {
      count++;
    }
    if (searchParams.classCategoryGroup) {
      count++;
    }
    if (inStockFilterEnabled) {
      count++;
    }
    if (searchParams.thcMin || searchParams.thcMax) {
      count++;
    }
    setTotalFiltersApplied(count);
  }, [searchParams]);

  return (
    <DrawerRoot open={isDrawerOpen} onOpenChange={(e) => setDrawerOpen(e.open)} size={'md'}>
      <DrawerTrigger asChild>
        <PillButton borderRadius={'6px'}>
          <SlidersIcon size={16} radius={'6px'} />{' '}
          <Text fontSize={'sm'} fontWeight={'semibold'}>
            Filters
          </Text>
        </PillButton>
      </DrawerTrigger>
      <Portal>
        <DrawerBackdrop />
        <DrawerPositioner>
          <DrawerContent ref={contentRef}>
            <DrawerHeader pb={3.5} pt={3.5} borderBottom={'1px solid var(--chakra-colors-border-transparent)'}>
              <Flex justifyContent={'space-between'} w="full" alignItems={'center'}>
                <Flex gap={2} alignItems={'center'}>
                  <DrawerTitle fontSize={'md'} fontWeight={'semibold'}>
                    Filters
                  </DrawerTitle>
                  <Badge className={styles.filterBadge}>{totalFiltersApplied}</Badge>
                </Flex>
                <Flex gap={2}>
                  <Button variant={'outline'} className={styles.clearButton} onClick={clearFilter}>
                    Clear All
                  </Button>
                  <DrawerCloseTrigger asChild pos="initial" height={'24px'}>
                    <CloseButton />
                  </DrawerCloseTrigger>
                </Flex>
              </Flex>
            </DrawerHeader>
            <DrawerBody>
              <AccordionRoot multiple>
                {/* Category */}
                <AccordionItem value={'category'} pt={2} pb={2}>
                  <AccordionItemTrigger cursor={'pointer'}>
                    <Flex justifyContent={'space-between'} w={'100%'}>
                      <Text className={styles.filterLabel}>
                        {page === LinkEnum.INVENTORY
                          ? categories?.[searchParams.inventoryClass || '']?.label
                          : 'Product category'}
                      </Text>
                      <Flex gap={2}>
                        <ClearFilter
                          text={'Clear'}
                          isVisible={!!searchParams.classCategoryGroup}
                          onClear={() => onSearch({ classCategoryGroup: undefined })}
                        />
                        <PinIcon name={Pin.Category} />
                      </Flex>
                    </Flex>
                  </AccordionItemTrigger>
                  <AccordionItemContent>
                    <CategoryAccordion />
                  </AccordionItemContent>
                </AccordionItem>
                {/* Brand */}
                {page !== LinkEnum.BRAND_DETAILS && (
                  <AccordionItem value={'brand'} pt={2} pb={2}>
                    <AccordionItemTrigger cursor={'pointer'}>
                      <Flex justifyContent={'space-between'} w={'full'}>
                        <Text className={styles.filterLabel}>Brand</Text>
                        <Flex gap={2}>
                          <ClearFilter
                            text={'Clear'}
                            isVisible={!!searchParams.brandId?.length}
                            onClear={() => onSearch({ brandId: undefined })}
                          />
                          <PinIcon name={Pin.Brand} />
                        </Flex>
                      </Flex>
                    </AccordionItemTrigger>
                    <AccordionItemContent>
                      <BrandFilter showPills={true} />
                    </AccordionItemContent>
                  </AccordionItem>
                )}
                {/* Strain Type */}
                <AccordionItem value={'strainType'} pt={2} pb={2}>
                  <AccordionItemTrigger cursor={'pointer'}>
                    <Flex justifyContent={'space-between'} w={'100%'}>
                      <Text className={styles.filterLabel}>Strain Type</Text>
                      <Flex gap={2}>
                        <ClearFilter
                          text={'Clear'}
                          isVisible={!!searchParams.strainType}
                          onClear={() => onSearch({ strainType: undefined })}
                        />
                        <PinIcon name={Pin.StrainType} />
                      </Flex>
                    </Flex>
                  </AccordionItemTrigger>
                  <AccordionItemContent>
                    <StrainType />
                    {/* <StrainFilter ref={contentRef} /> */}
                  </AccordionItemContent>
                </AccordionItem>
                {/* Weight */}
                <AccordionItem value={'weight-range'} pt={2} pb={2}>
                  <AccordionItemTrigger cursor={'pointer'}>
                    <Flex justifyContent={'space-between'} w={'100%'}>
                      <Text className={styles.filterLabel}>Product weight (g)</Text>
                      <Flex gap={2}>
                        <ClearFilter
                          text={'Clear'}
                          isVisible={!!searchParams.weightRange}
                          onClear={() => onSearch({ weightRange: undefined })}
                        />
                        <PinIcon name={Pin.WeightRange} />
                      </Flex>
                    </Flex>
                  </AccordionItemTrigger>
                  <AccordionItemContent pl={1} pr={1}>
                    <Weight />
                  </AccordionItemContent>
                </AccordionItem>
                {/* CBD to THC Ratio */}
                <AccordionItem value={'cbdThcRatio'} pt={2} pb={2}>
                  <AccordionItemTrigger cursor={'pointer'}>
                    <Flex justifyContent={'space-between'} w={'100%'}>
                      <Text className={styles.filterLabel}>CBD to THC Ratio</Text>
                      <Flex gap={2}>
                        <ClearFilter
                          text={'Clear'}
                          isVisible={!!searchParams.cbdThcRatio}
                          onClear={() => onSearch({ cbdThcRatio: undefined })}
                        />
                        <PinIcon name={Pin.CbdThcRatio} />
                      </Flex>
                    </Flex>
                  </AccordionItemTrigger>
                  <AccordionItemContent pl={1} pr={1}>
                    <CbdThcRatio />
                  </AccordionItemContent>
                </AccordionItem>
                {/* Price Filter */}
                {canViewPricing && (
                  <AccordionItem value={'price'} pt={2} pb={2}>
                    <AccordionItemTrigger cursor={'pointer'}>
                      <Flex justifyContent={'space-between'} w={'100%'}>
                        <Text className={styles.filterLabel}>Price per unit</Text>
                        <Flex gap={2}>
                          <ClearFilter
                            text={'Reset'}
                            isVisible={!!searchParams.priceMin || !!searchParams.priceMax}
                            onClear={() => onSearch({ priceMin: undefined, priceMax: undefined })}
                          />
                          <PinIcon name={Pin.Price} />
                        </Flex>
                      </Flex>
                    </AccordionItemTrigger>
                    <AccordionItemContent pl={1} pr={1}>
                      <PriceSlider />
                    </AccordionItemContent>
                  </AccordionItem>
                )}
                {/* Expiration Date */}
                <AccordionItem value={'expirationDate'} pt={2} pb={2}>
                  <AccordionItemTrigger cursor={'pointer'}>
                    <Flex justifyContent={'space-between'} w={'100%'}>
                      <Text className={styles.filterLabel}>Expiration date</Text>
                      <Flex gap={2}>
                        <ClearFilter
                          text={'Clear'}
                          isVisible={!!searchParams.expirationDays}
                          onClear={() => onSearch({ expirationDays: undefined })}
                        />
                        <PinIcon name={Pin.ExpirationDays} />
                      </Flex>
                    </Flex>
                  </AccordionItemTrigger>
                  <AccordionItemContent>
                    <ExpirationDays />
                  </AccordionItemContent>
                </AccordionItem>
                {/* THC Percentage */}
                <AccordionItem value={'thcPercentage'} pt={2} pb={2}>
                  <AccordionItemTrigger cursor={'pointer'}>
                    <Flex justifyContent={'space-between'} w={'100%'}>
                      <Text className={styles.filterLabel}>THC</Text>
                      <Flex gap={2}>
                        <ClearFilter
                          text={'Reset'}
                          isVisible={
                            !!searchParams.thcMin ||
                            !!searchParams.thcMax ||
                            !!searchParams.thcWeightMin ||
                            !!searchParams.thcWeightMax
                          }
                          onClear={() =>
                            onSearch({
                              thcMin: undefined,
                              thcMax: undefined,
                              thcWeightMin: undefined,
                              thcWeightMax: undefined,
                            })
                          }
                        />
                        <PinIcon name={Pin.THC} />
                      </Flex>
                    </Flex>
                  </AccordionItemTrigger>
                  <AccordionItemContent pl={1} pr={1}>
                    <ThcSlider />
                  </AccordionItemContent>
                </AccordionItem>
                {/* Product Availability */}
                {page !== LinkEnum.SEARCH && !isPublicPage && (
                  <AccordionItem value={'productAvailability'} pt={2} pb={2}>
                    <AccordionItemTrigger cursor={'pointer'}>
                      <Flex justifyContent={'space-between'} w={'100%'}>
                        <Text className={styles.filterLabel}>Product availability</Text>
                        <Flex gap={2}>
                          <ClearFilter
                            text={'Clear'}
                            isVisible={inStockFilterEnabled}
                            onClear={() => {
                              setInStockFilter(false);
                              onSearch({});
                            }}
                          />
                          <PinIcon name={Pin.ProductAvailibility} />
                        </Flex>
                      </Flex>
                    </AccordionItemTrigger>
                    <AccordionItemContent>
                      <ProductAvailability />
                    </AccordionItemContent>
                  </AccordionItem>
                )}
              </AccordionRoot>
            </DrawerBody>
          </DrawerContent>
        </DrawerPositioner>
      </Portal>
    </DrawerRoot>
  );
};

export default FiltersDrawer;
